import React, { useEffect, useMemo, useState } from "react";
import { Alert, Collapse, Form } from "react-bootstrap";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  fetchPublicTableSheet,
  fetchTableBookingLayout,
  fetchTableBookingLayouts,
  fetchTableSheetShareLink,
} from "../../../../api/tableBookingApi";
import CustomToggleButton from "../../../customToggleButton/customToggleButton";
import ConfirmModal from "../../../confirmModal/confirmModel";
import CustomeSelect from "../../../customSelect/customSelect";
import {
  buildTableBookingAreaPricingPayload,
  flattenLayoutPricingRows,
  formatEffectivePriceHint,
  formatEffectivePricePlaceholder,
  // formatLayoutDisplayLabelPlaceholder, // used only by the hidden price-display-label field
  resolveEffectiveEventPricing,
  indexAreaPricingRows,
  MANUAL_AVAILABILITY_VALUES,
  normalizeLayoutForEventPricing,
  normalizeManualAvailability,
  removeAreaPricingRow,
  resolveEventChannelSoldOut,
  rowHasOverride,
  toOptionalPartySize,
} from "../../../../helpers/eventTableBooking";
import {
  indexLiveSheetTables,
  resolveDisplayedManualAvailability,
} from "../../../../helpers/eventTableBookingLiveStatus";
import {
  unwrapTableSheetResponse,
  unwrapTableSheetShareLink,
} from "../../../../helpers/tableSheet";
import {
  blockWheelChangeOnNumberInput,
  layoutRecordId,
  manualAvailabilityButtonLabel,
  normalizeLayoutDetail,
  normalizeLayoutsList,
} from "../../../../pages/tableBooking/tableBookingUtils";
import "./eventTableBooking.css";

const LIVE_POLL_MS = 30000;

const EventTableBookingSection = ({
  control,
  setValue,
  effectiveClubId,
  eventId,
  showHeading = true,
  showToggle = true,
}) => {
  const { t } = useTranslation();
  const [layoutsLoading, setLayoutsLoading] = useState(false);
  const [layoutsError, setLayoutsError] = useState("");
  const [layouts, setLayouts] = useState([]);
  const [layoutDetail, setLayoutDetail] = useState(null);
  const [layoutDetailLoading, setLayoutDetailLoading] = useState(false);
  const [expandedZoneIds, setExpandedZoneIds] = useState(() => new Set());
  const [liveIndex, setLiveIndex] = useState(null);
  const [pendingChannelChange, setPendingChannelChange] = useState(null);

  const enabled = useWatch({ control, name: "tableBookingEnabled", defaultValue: false });
  const layoutId = useWatch({ control, name: "tableBookingLayoutId", defaultValue: "" });
  const slotKey = useWatch({ control, name: "tableBookingSlotKey", defaultValue: "" });
  const areaPricing = useWatch({ control, name: "tableBookingAreaPricing", defaultValue: [] });
  const watchedClub = useWatch({ control, name: "club" });

  const resolvedClubId = useMemo(() => {
    if (effectiveClubId) return String(effectiveClubId);
    const clubId = watchedClub?.id;
    return clubId != null && clubId !== "" ? String(clubId) : null;
  }, [effectiveClubId, watchedClub]);

  useEffect(() => {
    if (!enabled || layoutsLoading || !layouts.length) return;
    if (String(layoutId ?? "").trim()) return;
    const layout = layouts[0];
    if (!layout) return;
    setValue("tableBookingLayoutId", layoutRecordId(layout), {
      shouldDirty: true,
    });
    setValue("tableBookingViewkey", String(layout?.viewkey ?? "").toLowerCase(), {
      shouldDirty: true,
    });
    const firstSlot = layout?.slots?.[0]?.key;
    if (firstSlot) {
      setValue("tableBookingSlotKey", String(firstSlot), { shouldDirty: true });
    }
  }, [enabled, layouts, layoutsLoading, layoutId, setValue]);

  useEffect(() => {
    if (!resolvedClubId) {
      setLayouts([]);
      return;
    }
    let cancelled = false;
    setLayoutsLoading(true);
    setLayoutsError("");
    fetchTableBookingLayouts(resolvedClubId)
      .then((res) => {
        if (cancelled) return;
        setLayouts(normalizeLayoutsList(res));
      })
      .catch((err) => {
        if (cancelled) return;
        setLayouts([]);
        setLayoutsError(
          typeof err === "string"
            ? err
            : err?.message || t("TABLE_BOOKING_LAYOUTS_LOAD_ERROR")
        );
      })
      .finally(() => {
        if (!cancelled) setLayoutsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedClubId, t]);

  useEffect(() => {
    if (!enabled || !resolvedClubId || !String(layoutId ?? "").trim()) {
      setLayoutDetail(null);
      return;
    }
    const listMatch = layouts.find(
      (l) => layoutRecordId(l) === String(layoutId)
    );
    let cancelled = false;
    setLayoutDetailLoading(true);
    fetchTableBookingLayout(resolvedClubId, layoutId)
      .then((res) => {
        if (cancelled) return;
        const detail = normalizeLayoutDetail(res);
        setLayoutDetail(
          normalizeLayoutForEventPricing(detail || listMatch) || null
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLayoutDetail(normalizeLayoutForEventPricing(listMatch) || null);
        }
      })
      .finally(() => {
        if (!cancelled) setLayoutDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, resolvedClubId, layoutId, layouts]);

  const resolvedEventId = String(eventId ?? "").trim();

  // Highlight Manual availability from live bookings (booked→SOLD OUT, hold→UNAVAILABLE).
  useEffect(() => {
    if (!enabled || !resolvedClubId || !resolvedEventId) {
      setLiveIndex(null);
      return;
    }

    let cancelled = false;
    let intervalId = null;

    const loadLive = async () => {
      try {
        const shareRaw = await fetchTableSheetShareLink(resolvedClubId);
        if (cancelled) return;
        const share = unwrapTableSheetShareLink(shareRaw);
        const slug = String(share?.slug || "").trim().toLowerCase();
        if (!slug) {
          if (!cancelled) setLiveIndex(null);
          return;
        }

        const sheetRaw = await fetchPublicTableSheet(slug, {
          eventId: resolvedEventId,
        });
        if (cancelled) return;
        const payload = unwrapTableSheetResponse(sheetRaw);
        setLiveIndex(indexLiveSheetTables(payload?.sheet || null));
      } catch {
        if (!cancelled) setLiveIndex(null);
      }
    };

    loadLive();
    intervalId = setInterval(loadLive, LIVE_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") loadLive();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, resolvedClubId, resolvedEventId]);

  const selectedLayout = useMemo(() => {
    if (!layoutId) return null;
    const raw =
      layoutDetail &&
      layoutRecordId(layoutDetail) === String(layoutId)
        ? layoutDetail
        : layouts.find((l) => layoutRecordId(l) === String(layoutId)) ?? null;
    return normalizeLayoutForEventPricing(raw);
  }, [layouts, layoutId, layoutDetail]);

  const layoutOptions = useMemo(
    () =>
      layouts.map((l) => ({
        label: `${l?.title || t("TABLE_BOOKING_LAYOUT_EDIT")} (${l?.viewkey || layoutRecordId(l)})`,
        value: layoutRecordId(l),
        id: layoutRecordId(l),
        viewkey: String(l?.viewkey ?? "").toLowerCase(),
      })),
    [layouts, t]
  );

  useEffect(() => {
    if (!enabled || !selectedLayout || layoutDetailLoading) return;

    const layoutViewkey = String(selectedLayout?.viewkey ?? "").toLowerCase();
    if (layoutViewkey) {
      setValue("tableBookingViewkey", layoutViewkey, { shouldDirty: false });
    }

    const firstSlot = selectedLayout?.slots?.[0]?.key;
    if (firstSlot && !String(slotKey ?? "").trim()) {
      setValue("tableBookingSlotKey", String(firstSlot), { shouldDirty: true });
    }
  }, [enabled, selectedLayout, layoutDetailLoading, slotKey, setValue]);

  const pricingRows = useMemo(
    () => flattenLayoutPricingRows(selectedLayout),
    [selectedLayout]
  );

  const pricingByZone = useMemo(() => {
    const zones = pricingRows.filter((r) => r.kind === "zone");
    const tables = pricingRows.filter((r) => r.kind === "table");
    return zones.map((zone) => ({
      ...zone,
      tables: tables.filter((t) => t.areaId === zone.areaId),
    }));
  }, [pricingRows]);

  const zoneIdsKey = useMemo(
    () => pricingByZone.map((z) => z.areaId).join(","),
    [pricingByZone]
  );

  const firstZoneId = pricingByZone[0]?.areaId ?? null;

  useEffect(() => {
    setExpandedZoneIds(firstZoneId ? new Set([firstZoneId]) : new Set());
  }, [layoutId, zoneIdsKey, firstZoneId]);

  const toggleZoneExpand = (areaId) => {
    setExpandedZoneIds((current) => {
      const next = new Set(current);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const overrideIndex = useMemo(
    () => indexAreaPricingRows(areaPricing),
    [areaPricing]
  );

  const syncLayoutSelection = (nextLayoutId) => {
    const layout = layouts.find(
      (l) => layoutRecordId(l) === String(nextLayoutId)
    );
    if (!layout) return;
    setValue("tableBookingLayoutId", layoutRecordId(layout), {
      shouldDirty: true,
    });
    setValue("tableBookingViewkey", String(layout?.viewkey ?? "").toLowerCase(), {
      shouldDirty: true,
    });
    const firstSlot = layout?.slots?.[0]?.key;
    if (firstSlot) {
      setValue("tableBookingSlotKey", String(firstSlot), { shouldDirty: true });
    }
  };

  /**
   * Merge a patch into the override row for a zone/table. The row is dropped
   * when it no longer changes anything vs. the layout. Captures the layout
   * default price/currency so the API always receives the required
   * priceFromPerPerson even for availability/party-size-only overrides.
   */
  const mergeOverridePatch = (currentRows, row, patch) => {
    const { areaId, tableId, layoutDefault } = row;
    const key = tableId ? `${areaId}:${tableId}` : areaId;
    const existing = indexAreaPricingRows(currentRows).get(key);
    const base = existing
      ? { ...existing }
      : {
          areaId,
          ...(tableId ? { tableId } : {}),
          priceFromPerPerson: "",
          currency: layoutDefault?.currency || "INR",
          priceDisplayLabel: "",
          minPartySize: "",
          maxPartySize: "",
          description: "",
          manualAvailability: "AUTO",
        };
    base.layoutPrice = layoutDefault?.price || base.layoutPrice || 0;

    const merged = { ...base, ...patch };
    let next = removeAreaPricingRow(currentRows || [], areaId, tableId);
    if (rowHasOverride(merged)) next = [...next, merged];
    return next;
  };

  const updateOverrideField = (row, patch) => {
    let next = mergeOverridePatch(areaPricing || [], row, patch);
    if (
      !row.tableId &&
      (Object.prototype.hasOwnProperty.call(patch, "onlineSoldOut") ||
        Object.prototype.hasOwnProperty.call(patch, "offlineSoldOut"))
    ) {
      const zone = pricingByZone.find((z) => z.areaId === row.areaId);
      for (const table of zone?.tables || []) {
        next = mergeOverridePatch(next, table, patch);
      }
    }
    setValue("tableBookingAreaPricing", next, { shouldDirty: true });
  };

  const requestChannelSoldOutChange = (row, field, current, checked) => {
    if (Boolean(current) === Boolean(checked)) return;
    setPendingChannelChange({
      row,
      field,
      checked: Boolean(checked),
    });
  };

  const closeChannelConfirm = () => setPendingChannelChange(null);

  const confirmChannelChange = () => {
    if (!pendingChannelChange) return;
    updateOverrideField(pendingChannelChange.row, {
      [pendingChannelChange.field]: pendingChannelChange.checked,
    });
    setPendingChannelChange(null);
  };

  const channelConfirmLabel =
    pendingChannelChange?.field === "offlineSoldOut"
      ? t("TABLE_BOOKING_OFFLINE_SOLD_OUT")
      : t("TABLE_BOOKING_ONLINE_SOLD_OUT");
  const channelConfirmState = pendingChannelChange?.checked
    ? t("TABLE_BOOKING_STATE_ON")
    : t("TABLE_BOOKING_STATE_OFF");

  const clearAllOverrides = () => {
    setValue("tableBookingAreaPricing", [], { shouldDirty: true });
  };

  const renderLayoutPricePill = (row) => {
    const effective = resolveEffectiveEventPricing(row, overrideIndex);
    const hint = formatEffectivePriceHint(effective, row.layoutDefault);
    const unset = hint === "Not set in layout";
    const labelKey =
      effective.source === "layout"
        ? "EVENT_TABLE_BOOKING_LAYOUT_PRICE"
        : "EVENT_TABLE_BOOKING_EVENT_PRICE_PILL";
    return (
      <span className={`etb-layout-pill ${unset ? "is-unset" : ""}`}>
        {t(labelKey)}: {hint}
      </span>
    );
  };

  const renderCardHead = ({ title, subtitle, pill }) => (
    <div className="etb-card-head">
      <div className="etb-card-head-left">
        {subtitle ? (
          <div className="etb-table-title-row">
            <span className="etb-table-icon" aria-hidden="true">
              <i className="mdi mdi-table-furniture" />
            </span>
            <div>
              <p className="etb-card-title mb-0">{title}</p>
              <p className="etb-card-sub mb-0">{subtitle}</p>
            </div>
          </div>
        ) : (
          <p className="etb-card-title mb-0">{title}</p>
        )}
      </div>
      {pill}
    </div>
  );

  const renderPricingInputs = (row, { nestedTables = null } = {}) => {
    const override = overrideIndex.get(
      row.tableId ? `${row.areaId}:${row.tableId}` : row.areaId
    );
    const effective = resolveEffectiveEventPricing(row, overrideIndex);
    const currency =
      override?.currency || row.layoutDefault?.currency || "INR";
    const layoutDefault = row.layoutDefault || {};
    const savedAvailability = normalizeManualAvailability(
      override?.manualAvailability ?? layoutDefault.manualAvailability
    );
    const activeAvailability = resolveDisplayedManualAvailability(
      row,
      liveIndex,
      savedAvailability,
      nestedTables
    );
    const minValue =
      override?.minPartySize != null ? override.minPartySize : "";
    const maxValue =
      override?.maxPartySize != null ? override.maxPartySize : "";
    const minNum = toOptionalPartySize(minValue);
    const maxNum = toOptionalPartySize(maxValue);
    const partyRangeInvalid =
      minNum != null && maxNum != null && minNum > maxNum;
    const minPlaceholder =
      layoutDefault.minPartySize != null
        ? String(layoutDefault.minPartySize)
        : t("EVENT_TABLE_BOOKING_INHERIT_LAYOUT");
    const maxPlaceholder =
      layoutDefault.maxPartySize != null
        ? String(layoutDefault.maxPartySize)
        : t("EVENT_TABLE_BOOKING_INHERIT_LAYOUT");
    const onlineSoldOutMeta = resolveEventChannelSoldOut(
      row,
      overrideIndex,
      "onlineSoldOut"
    );
    const offlineSoldOutMeta = resolveEventChannelSoldOut(
      row,
      overrideIndex,
      "offlineSoldOut"
    );
    const onlineInheritedFromZone =
      Boolean(row.tableId) &&
      onlineSoldOutMeta.inherited &&
      onlineSoldOutMeta.source === "zone";
    const offlineInheritedFromZone =
      Boolean(row.tableId) &&
      offlineSoldOutMeta.inherited &&
      offlineSoldOutMeta.source === "zone";

    return (
      <div className="etb-fields">
        <div className="etb-price-grid">
          {/* Event price/person override. Blank = inherit the layout default
              (placeholder shows it). Drives the contract-required
              priceFromPerPerson for this row. */}
          <div className="etb-field">
            <label className="etb-field-label">
              {t("EVENT_TABLE_BOOKING_COL_EVENT_PRICE")}
            </label>
            <Form.Control
              type="number"
              min="0"
              step="any"
              className="etb-input"
              placeholder={formatEffectivePricePlaceholder(
                effective,
                layoutDefault,
                t
              )}
              value={
                override?.priceFromPerPerson != null &&
                override.priceFromPerPerson !== ""
                  ? override.priceFromPerPerson
                  : ""
              }
              onWheel={blockWheelChangeOnNumberInput}
              onChange={(e) =>
                updateOverrideField(row, { priceFromPerPerson: e.target.value })
              }
            />
          </div>
          {/* Currency for the override price; defaults to the layout currency. */}
          <div className="etb-field etb-field-currency">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_CURRENCY")}
            </label>
            <Form.Control
              className="etb-input"
              value={currency}
              onChange={(e) =>
                updateOverrideField(row, { currency: e.target.value })
              }
            />
          </div>
          {/* Price display label override is intentionally hidden from the UI.
              State + payload handling (priceDisplayLabel) stay intact, so any
              value coming from the API still round-trips. Uncomment to re-enable. */}
          {/*
          <div className="etb-field etb-field-wide">
            <label className="etb-field-label">
              {t("EVENT_TABLE_BOOKING_DISPLAY_LABEL")}
            </label>
            <Form.Control
              className="etb-input"
              placeholder={formatLayoutDisplayLabelPlaceholder(layoutDefault)}
              value={override?.priceDisplayLabel || ""}
              onChange={(e) =>
                updateOverrideField(row, { priceDisplayLabel: e.target.value })
              }
            />
          </div>
          */}
        </div>

        <div className="etb-meta-grid">
          {/* Smallest party allowed to book this zone/table for this event.
              Blank = inherit layout min. Enforced at booking init + pax upgrade. */}
          <div className="etb-field">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_MIN_PARTY")}
            </label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              className={`etb-input ${partyRangeInvalid ? "is-invalid" : ""}`}
              placeholder={minPlaceholder}
              value={minValue}
              onWheel={blockWheelChangeOnNumberInput}
              onChange={(e) =>
                updateOverrideField(row, { minPartySize: e.target.value })
              }
            />
          </div>
          {/* Largest party allowed. Blank = inherit layout max. Must be >= min
              (validated inline below and again before submit). */}
          <div className="etb-field">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_MAX_PARTY")}
            </label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              className={`etb-input ${partyRangeInvalid ? "is-invalid" : ""}`}
              placeholder={maxPlaceholder}
              value={maxValue}
              onWheel={blockWheelChangeOnNumberInput}
              onChange={(e) =>
                updateOverrideField(row, { maxPartySize: e.target.value })
              }
            />
          </div>
          {/* Manual availability override for this event only: AUTO lets live
              booking counters decide; SOLD_OUT / UNAVAILABLE force-block booking
              and show the matching legend/pin. */}
          <div className="etb-field etb-field-availability">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_MANUAL_AVAIL")}
            </label>
            <div className="etb-avail-group">
              {MANUAL_AVAILABILITY_VALUES.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`etb-avail-btn is-${k.toLowerCase()} ${
                    activeAvailability === k ? "is-active" : ""
                  }`}
                  onClick={() =>
                    updateOverrideField(row, { manualAvailability: k })
                  }
                >
                  {manualAvailabilityButtonLabel(k, t)}
                </button>
              ))}
            </div>
          </div>
          <div className="etb-field etb-field-channel-soldout">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_ONLINE_SOLD_OUT")}
            </label>
            <div className="etb-channel-soldout-row">
              <CustomToggleButton
                id={`etb-online-${row.areaId}-${row.tableId || "zone"}`}
                checked={onlineSoldOutMeta.checked}
                disabled={onlineInheritedFromZone}
                onCheckChange={(checked) =>
                  requestChannelSoldOutChange(
                    row,
                    "onlineSoldOut",
                    onlineSoldOutMeta.checked,
                    checked
                  )
                }
              />
            </div>
            <p className="etb-settings-hint mb-0">
              {onlineInheritedFromZone
                ? t("TABLE_BOOKING_CHANNEL_SOLD_OUT_INHERITED")
                : t("TABLE_BOOKING_ONLINE_SOLD_OUT_HINT")}
            </p>
          </div>
          <div className="etb-field etb-field-channel-soldout">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_OFFLINE_SOLD_OUT")}
            </label>
            <div className="etb-channel-soldout-row">
              <CustomToggleButton
                id={`etb-offline-${row.areaId}-${row.tableId || "zone"}`}
                checked={offlineSoldOutMeta.checked}
                disabled={offlineInheritedFromZone}
                onCheckChange={(checked) =>
                  requestChannelSoldOutChange(
                    row,
                    "offlineSoldOut",
                    offlineSoldOutMeta.checked,
                    checked
                  )
                }
              />
            </div>
            <p className="etb-settings-hint mb-0">
              {offlineInheritedFromZone
                ? t("TABLE_BOOKING_CHANNEL_SOLD_OUT_INHERITED")
                : t("TABLE_BOOKING_OFFLINE_SOLD_OUT_HINT")}
            </p>
          </div>
        </div>

        {/* Inline guard: block obviously invalid min > max ranges before submit. */}
        {partyRangeInvalid ? (
          <p className="etb-field-error">
            {t("EVENT_TABLE_BOOKING_ERR_PARTY_SIZE")}
          </p>
        ) : null}

        {/* Optional per-event description override (e.g. tweaked booth blurb).
            Blank = inherit the layout description. */}
        <div className="etb-field etb-field-wide">
          <label className="etb-field-label">
            {t("EVENT_TABLE_BOOKING_DESCRIPTION")}
          </label>
          <Form.Control
            as="textarea"
            rows={2}
            className="etb-input"
            placeholder={
              layoutDefault.description
                ? layoutDefault.description
                : t("EVENT_TABLE_BOOKING_INHERIT_LAYOUT")
            }
            value={override?.description || ""}
            onChange={(e) =>
              updateOverrideField(row, { description: e.target.value })
            }
          />
        </div>
      </div>
    );
  };

  const renderZoneCard = (zone) => {
    const isExpanded = expandedZoneIds.has(zone.areaId);

    return (
      <article
        className={`etb-zone-card ${isExpanded ? "is-expanded" : "is-collapsed"}`}
        key={zone.areaId}
      >
        <div className="etb-zone-accent" aria-hidden="true" />
        <div className="etb-zone-body">
          <button
            type="button"
            className="etb-zone-toggle"
            onClick={() => toggleZoneExpand(zone.areaId)}
            aria-expanded={isExpanded}
            aria-controls={`etb-zone-panel-${zone.areaId}`}
          >
            <span className="etb-zone-chevron" aria-hidden="true">
              <i
                className={`mdi ${
                  isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
                }`}
              />
            </span>
            <span className="etb-zone-toggle-main">
              <span className="etb-card-title">{zone.label}</span>
              {!isExpanded && zone.tables.length > 0 ? (
                <span className="etb-card-sub">
                  {t("EVENT_TABLE_BOOKING_NESTED_TABLE_COUNT", {
                    count: zone.tables.length,
                  })}
                </span>
              ) : null}
            </span>
            <span
              className="etb-zone-toggle-pill"
              onClick={(e) => e.stopPropagation()}
            >
              {renderLayoutPricePill(zone)}
            </span>
          </button>
          <Collapse in={isExpanded}>
            <div
              id={`etb-zone-panel-${zone.areaId}`}
              className="etb-zone-collapse-body"
            >
              <section className="etb-zone-main">
                {renderPricingInputs(zone, {
                  nestedTables: zone.tables.length > 0 ? zone.tables : null,
                })}
              </section>
              {zone.tables.length > 0 ? (
                <section className="etb-tables-branch">
                  <div className="etb-tables-list">
                    {zone.tables.map((table) => (
                      <div className="etb-table-card" key={table.tableId}>
                        {renderCardHead({
                          title: table.label,
                          subtitle: `${t("EVENT_TABLE_BOOKING_NESTED_TABLE")} · ${table.zoneLabel}`,
                          pill: renderLayoutPricePill(table),
                        })}
                        {renderPricingInputs(table)}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          </Collapse>
        </div>
      </article>
    );
  };

  const toggleControl = (
    <Controller
      name="tableBookingEnabled"
      control={control}
      render={({ field: { onChange, value } }) => (
        <CustomToggleButton
          name="tableBookingEnabled"
          checked={Boolean(value)}
          onCheckChange={(checked) => {
            onChange(checked);
            if (!checked) {
              clearAllOverrides();
            }
          }}
        />
      )}
    />
  );

  return (
    <div className="etb-root">
      {showHeading && showToggle ? (
        <div className="d-flex locationToggleStyle sponsoredEventsStyle align-items-center event-table-booking-toggle-row mb-2">
          <p className="form-label mb-0">{t("EVENT_TABLE_BOOKING_TITLE")}</p>
          <div
            className="toggleRightStyle ms-auto event-table-booking-header-toggle"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {toggleControl}
          </div>
        </div>
      ) : null}

      {!enabled ? null : (
        <>
          <p className="etb-help">{t("EVENT_TABLE_BOOKING_HELP")}</p>

          {!resolvedClubId ? (
            <Alert variant="warning" className="py-2 small etb-alert">
              {t("EVENT_TABLE_BOOKING_NEED_CLUB")}
            </Alert>
          ) : null}

          {resolvedClubId && (layoutsLoading || layoutDetailLoading) ? (
            <p className="etb-loading">{t("EVENT_TABLE_BOOKING_LOADING")}</p>
          ) : null}

          {resolvedClubId && layoutsError ? (
            <Alert variant="danger" className="py-2 small etb-alert">
              {layoutsError}
            </Alert>
          ) : null}

          {resolvedClubId &&
          !layoutsLoading &&
          !layouts.length &&
          !layoutsError ? (
            <Alert variant="warning" className="py-2 small etb-alert">
              {t("EVENT_TABLE_BOOKING_NO_LAYOUTS")}
            </Alert>
          ) : null}

          {resolvedClubId && layouts.length > 0 ? (
            <>
              <div className="etb-settings-card">
                <p className="etb-settings-title">
                  {t("EVENT_TABLE_BOOKING_LAYOUT_SETTINGS")}
                </p>
                <div className="etb-settings-layout">
                  <p className="etb-settings-label mb-2">
                    {t("EVENT_TABLE_BOOKING_LAYOUT")}
                  </p>
                  <Controller
                    name="tableBookingLayoutId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CustomeSelect
                        options={layoutOptions}
                        closeMenuOnSelect
                        isSearchable
                        placeholder={t("SELECT")}
                        value={
                          layoutOptions.find(
                            (o) => String(o.id) === String(value)
                          ) ?? null
                        }
                        onChange={(item) => {
                          const id = item?.id ?? "";
                          onChange(id);
                          if (id) syncLayoutSelection(id);
                        }}
                        className="singleSelecter organiserDropdownStyle"
                      />
                    )}
                  />
                </div>
              </div>

              {selectedLayout && pricingByZone.length > 0 ? (
                <>
                  <div className="etb-pricing-toolbar">
                    <div>
                      <p className="etb-pricing-title">
                        {t("EVENT_TABLE_BOOKING_PRICING_TITLE")}
                      </p>
                      <p className="etb-pricing-sub">
                        {t("EVENT_TABLE_BOOKING_PRICING_HELP")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="etb-clear-btn"
                      onClick={clearAllOverrides}
                    >
                      {t("EVENT_TABLE_BOOKING_CLEAR_OVERRIDES")}
                    </button>
                  </div>
                  <div className="etb-zone-list">
                    {pricingByZone.map((zone) => renderZoneCard(zone))}
                  </div>
                  <p className="etb-footer-note">
                    {t("EVENT_TABLE_BOOKING_OVERRIDE_COUNT", {
                      count: buildTableBookingAreaPricingPayload(areaPricing)
                        .length,
                    })}
                  </p>
                </>
              ) : selectedLayout && !layoutDetailLoading ? (
                <Alert variant="warning" className="py-2 small etb-alert">
                  {t("EVENT_TABLE_BOOKING_NO_AREAS")}
                </Alert>
              ) : null}
            </>
          ) : null}
        </>
      )}
      <ConfirmModal
        modalTitle={t("TABLE_BOOKING_CONFIRM_CHANNEL_TITLE")}
        modalDescription={t("TABLE_BOOKING_CONFIRM_CHANNEL_CHANGE", {
          channel: channelConfirmLabel,
          state: channelConfirmState,
        })}
        isUnderline={false}
        showModal={Boolean(pendingChannelChange)}
        leftBtnTitle={t("YES_CONFIRM")}
        isModelIcon={true}
        iconColor="mdi mdi-help-circle-outline"
        iconCricle="iconCricleCancel"
        leftBtnClass="ok_Btn"
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="borderButton"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={confirmChannelChange}
        rightButtonClick={closeChannelConfirm}
        onHide={closeChannelConfirm}
      />
    </div>
  );
};

export default EventTableBookingSection;
